/**
 * Scrollable transcript log — finalized turns only.
 *
 * Auto-scrolls to the latest turn, soft-fades older entries at the top.
 * Streaming partials live in `LivePartialStrip` so this list never gets
 * a flood of micro-bubbles.
 */

import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "./styles";

export function TranscriptPanel({ turns }) {
  const scrollerRef = useRef(null);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
  }, [turns]);

  if (!turns || turns.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          padding: "32px 24px",
          textAlign: "center",
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 500,
            color: tokens.ink[200],
          }}
        >
          Tap the mic to start a session
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.5,
            color: tokens.ink[300],
          }}
        >
          You'll be talking to Noki — your clinical voice copilot.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      style={{
        flex: 1,
        minHeight: 120,
        overflowY: "auto",
        padding: "16px 20px",
        scrollBehavior: "smooth",
        maskImage:
          "linear-gradient(180deg, transparent 0, black 32px, black calc(100% - 8px), transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(180deg, transparent 0, black 32px, black calc(100% - 8px), transparent 100%)",
      }}
    >
      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <AnimatePresence initial={false}>
          {turns.map((turn) => (
            <motion.li
              key={turn.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                alignItems: turn.role === "user" ? "flex-end" : "flex-start",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color:
                    turn.role === "user"
                      ? tokens.accent.cyan
                      : tokens.accent.violet,
                  opacity: 0.85,
                }}
              >
                {turn.role === "user" ? "You" : "Noki"}
              </span>
              <span
                style={{
                  maxWidth: "85%",
                  padding: "8px 14px",
                  borderRadius: 16,
                  border: `1px solid ${tokens.surface.border}`,
                  background:
                    turn.role === "user"
                      ? "rgba(255, 255, 255, 0.05)"
                      : tokens.surface.panelLight,
                  color: tokens.ink[50],
                  fontSize: 14,
                  lineHeight: 1.45,
                  backdropFilter: "blur(8px)",
                }}
              >
                {turn.text}
              </span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
    </div>
  );
}
